import React, { useState } from 'react'

// Hooks
import { useToast } from '@sketch/toasts'

// Components
import { useForTablet, Separator, DocumentHeader } from '@sketch/components'

// Local Components
import { ProjectHeaderActions } from './ProjectHeaderActions'

// Types
import {
  GetProjectsDocument,
  GetProjectsQueryVariables,
  useRenameProjectMutation,
} from '@sketch/gql-types'
import type { ProjectHeaderProps } from './types'

const typeInline = <T extends any>(variables: T) => variables

const DRAFTS_HEADER_COPY = {
  title: 'My Drafts',
  description:
    'Only you can see documents that you keep here, unless you share them.',
}

const ProjectHeader: React.FC<ProjectHeaderProps> = props => {
  const { workspace, project, userCanRenameAndDeleteProject } = props
  const { identifier: workspaceId } = workspace

  const { showToast } = useToast()
  const isTabletAndBigger = useForTablet()
  const isMobile = !isTabletAndBigger

  const [forceEdit, setForceEdit] = useState(false)

  const [renameProject] = useRenameProjectMutation({
    redirectErrors: true,
    onError: 'show-toast',
    refetchQueries: [
      {
        query: GetProjectsDocument,
        variables: typeInline<GetProjectsQueryVariables>({
          workspaceId,
        }),
      },
    ],
    awaitRefetchQueries: true,
    optimisticResponse: ({ name }) => ({
      __typename: 'RootMutationType',
      renameProject: {
        __typename: 'ProjectRenamePayload',
        errors: [],
        successful: true,
        project: {
          ...project,
          name,
        },
      },
    }),
    onCompleted: () => {
      showToast('Project was renamed successfully')
    },
  })

  const isDraft = project.type === 'PERSONAL_DRAFTS'

  const headerCopy: { title: string; description?: string } = isDraft
    ? DRAFTS_HEADER_COPY
    : { title: project?.name || '' }

  // Hide the Edit UI if the user doesn't have these permissions
  let handleEdit
  if (userCanRenameAndDeleteProject && !project.deletedAt) {
    handleEdit = async (title: string) => {
      await renameProject({
        variables: { name: title, projectId: project.identifier },
      })
    }
  }

  if (isMobile) {
    return (
      <>
        <ProjectHeaderActions {...props} onRenameProject={setForceEdit} />
        <Separator mb={4} mt={4} />
      </>
    )
  }

  return (
    <DocumentHeader
      title={headerCopy.title}
      description={headerCopy?.description}
      titlePlaceholder="Project name"
      actions={
        <ProjectHeaderActions {...props} onRenameProject={setForceEdit} />
      }
      onEdit={handleEdit}
      forceEdit={forceEdit}
      onClearForceEdit={() => setForceEdit(false)}
    />
  )
}

export default ProjectHeader
